import type { DiagramType } from "./types";

const MAX_SOURCE_LENGTH = 8_000;
const MAX_LINES = 120;
const MAX_RELATIONS = 90;
const FORBIDDEN = [
  /%%\{/i,
  /\bclick\b/i,
  /\bhref\b/i,
  /javascript\s*:/i,
  /<\/?(?:script|iframe|object|embed|foreignObject|style|link|meta)\b/i,
  /\bclassDef\b[^\n]*(?:url|expression)\s*\(/i,
];

export const RENDER_FIXTURES: Record<DiagramType, string> = {
  flowchart: `flowchart LR\n  A["Wish"] --> B{"Bright enough?"}\n  B -- Yes --> C["Pocket star"]\n  B -- Not yet --> A`,
  mindmap: `mindmap\n  root((Impossible garden))\n    Moon flowers\n      Open at noon\n    Clockwork bees\n      Collect seconds`,
  sequence: `sequenceDiagram\n  participant L as LittleBot\n  participant O as OrbitBot\n  L->>O: Can a map feel homesick?\n  O-->>L: Only if it remembers north.`,
  state: `stateDiagram-v2\n  [*] --> Curious\n  Curious --> Delighted: finds a tiny moon\n  Delighted --> [*]`,
  class: `classDiagram\n  class DreamMachine {\n    +collectWish()\n    +makeStar()\n  }\n  DreamMachine --> PocketStar`,
  timeline: `timeline\n  title A brief history of tomorrow\n  section Morning\n  08h00 : Someone invents a cloud pocket\n  08h05 : It starts raining indoors`,
};

export interface MermaidValidation {
  diagramType: DiagramType;
  lineCount: number;
  relationCount: number;
}

export interface MermaidIntegrityIssue {
  code: "stale-explicit-node-reference" | "unexpected-source-only-node" | "ambiguous-conditional-branch";
  nodeId: string;
  previousLabel?: string;
  message: string;
}

export interface MermaidIntegrityReport {
  category: "valid" | "structurally-suspicious";
  issues: MermaidIntegrityIssue[];
}

interface ParsedFlowNode {
  id: string;
  explicitlyDeclared: boolean;
  label?: string;
}

interface ParsedFlowEdge {
  from: string;
  to: string;
  label?: string;
}

interface FlowStructureCollector {
  nodes: Map<string, ParsedFlowNode>;
  edges: ParsedFlowEdge[];
}

export function detectDiagramType(source: string): DiagramType {
  const first = source.trimStart().split(/\r?\n/, 1)[0]?.trim() ?? "";
  if (/^(?:flowchart|graph)\s+(?:TB|TD|BT|RL|LR)\b/i.test(first)) return "flowchart";
  if (/^mindmap\b/i.test(first)) return "mindmap";
  if (/^sequenceDiagram\b/i.test(first)) return "sequence";
  if (/^stateDiagram(?:-v2)?\b/i.test(first)) return "state";
  if (/^classDiagram\b/i.test(first)) return "class";
  if (/^timeline\b/i.test(first)) return "timeline";
  throw new Error("Unsupported Mermaid diagram type");
}

/** Static safety and complexity envelope. This is not a Mermaid parser. */
export function validateMermaidSource(source: string, expectedType?: DiagramType): MermaidValidation {
  if (!source.trim()) throw new Error("Mermaid source is empty");
  if (source.length > MAX_SOURCE_LENGTH) throw new Error(`Mermaid source exceeds ${MAX_SOURCE_LENGTH} characters`);
  const lines = source.split(/\r?\n/);
  if (lines.length > MAX_LINES) throw new Error(`Mermaid source exceeds ${MAX_LINES} lines`);
  for (const pattern of FORBIDDEN) if (pattern.test(source)) throw new Error("Mermaid source contains a forbidden interactive or unsafe directive");
  if (/<\s*\/?\s*[a-z][^>]*>/i.test(source)) throw new Error("HTML tags are not allowed in Mermaid labels");

  const diagramType = detectDiagramType(source);
  if (expectedType && diagramType !== expectedType) throw new Error(`Expected ${expectedType} but received ${diagramType}`);
  const relationCount = (source.match(/-->|---|->>|-->>|\.->|==>|\bclass\s+|:\s/g) ?? []).length;
  if (relationCount > MAX_RELATIONS) throw new Error(`Mermaid source exceeds ${MAX_RELATIONS} relationships`);

  const hasBody = lines.slice(1).some((line) => line.trim() && !line.trim().startsWith("%%"));
  if (!hasBody) throw new Error("Mermaid diagram has no body");
  const requirements: Record<DiagramType, RegExp> = {
    flowchart: /-->|---|==>|\.->/,
    mindmap: /^\s{2,}\S/m,
    sequence: /(?:->>|-->>|->|-->)/,
    state: /-->/,
    class: /\bclass\s+[A-Za-z_]/,
    timeline: /^\s+[^\n:]+\s*:/m,
  };
  if (!requirements[diagramType].test(source)) throw new Error(`${diagramType} diagram lacks its required structure`);
  return { diagramType, lineCount: lines.length, relationCount };
}

/**
 * Runs the actual Mermaid 12.2 grammar in a DOM-free Worker context after the
 * safety envelope. It proves syntax acceptance, not browser layout/rendering.
 */
export async function validateMermaidSyntaxWithWorkerGrammar(source: string, expectedType?: DiagramType): Promise<MermaidValidation> {
  const validation = validateMermaidSource(source, expectedType);
  try {
    await parseWithMermaidGrammar(source, validation.diagramType);
  } catch (error) {
    const message = error instanceof Error ? error.message : "unknown Mermaid parser error";
    throw new Error(`Mermaid parser rejected source: ${message}`);
  }
  return validation;
}

function readableNodeLabel(value: unknown): string | undefined {
  if (typeof value === "string") return value.trim() || undefined;
  if (!value || typeof value !== "object") return undefined;
  const text = (value as { text?: unknown }).text;
  if (typeof text === "string") return text.trim() || undefined;
  if (Array.isArray(text)) {
    const joined = text.map((item) => typeof item === "string" ? item : "").join("").trim();
    return joined || undefined;
  }
  return undefined;
}

function recordFlowNode(collector: FlowStructureCollector, idValue: unknown, text: unknown, type: unknown): void {
  if (typeof idValue !== "string" || !idValue.trim()) return;
  const id = idValue.trim();
  const existing = collector.nodes.get(id);
  const label = readableNodeLabel(text);
  const explicitlyDeclared = text !== undefined || type !== undefined;
  collector.nodes.set(id, {
    id,
    explicitlyDeclared: Boolean(existing?.explicitlyDeclared || explicitlyDeclared),
    ...(label || existing?.label ? { label: label ?? existing?.label } : {}),
  });
}

function recordFlowLinks(collector: FlowStructureCollector, starts: unknown, ends: unknown, linkData: unknown): void {
  if (!Array.isArray(starts) || !Array.isArray(ends)) return;
  const text = linkData && typeof linkData === "object" ? (linkData as { text?: { text?: unknown } }).text?.text : undefined;
  const label = typeof text === "string" ? text.replace(/^['"]|['"]$/g, "").trim() : "";
  for (const from of starts) for (const to of ends) {
    if (typeof from !== "string" || typeof to !== "string") continue;
    collector.edges.push({ from, to, ...(label ? { label } : {}) });
  }
}

async function parseFlowStructure(source: string): Promise<FlowStructureCollector> {
  const collector: FlowStructureCollector = { nodes: new Map(), edges: [] };
  await parseWithMermaidGrammar(source, "flowchart", collector);
  return collector;
}

export async function inspectMermaidIntegrity(source: string, expectedType: DiagramType, baseSource?: string): Promise<MermaidIntegrityReport> {
  await validateMermaidSyntaxWithWorkerGrammar(source, expectedType);
  if (expectedType !== "flowchart" || !baseSource) return { category: "valid", issues: [] };
  await validateMermaidSyntaxWithWorkerGrammar(baseSource, "flowchart");
  // Mermaid's version-pinned parser object carries mutable `yy` state, so these
  // two grammar passes must remain sequential within one isolate.
  const base = await parseFlowStructure(baseSource);
  const final = await parseFlowStructure(source);
  const issues: MermaidIntegrityIssue[] = [];
  for (const [nodeId, finalNode] of final.nodes) {
    const baseNode = base.nodes.get(nodeId);
    if (!baseNode?.explicitlyDeclared || finalNode.explicitlyDeclared) continue;
    issues.push({
      code: "stale-explicit-node-reference",
      nodeId,
      ...(baseNode.label ? { previousLabel: baseNode.label } : {}),
      message: `Node ${nodeId} was explicitly labelled in the foundation but survives only as an implicit ID reference`,
    });
  }
  const incoming = (edges: ParsedFlowEdge[], nodeId: string) => edges.filter((edge) => edge.to === nodeId);
  const outgoing = (edges: ParsedFlowEdge[], nodeId: string) => edges.filter((edge) => edge.from === nodeId);
  for (const [nodeId, finalNode] of final.nodes) {
    if (!finalNode.explicitlyDeclared) continue; // already covered by stale-explicit-node-reference
    if (incoming(base.edges, nodeId).length === 0 || incoming(final.edges, nodeId).length > 0 || outgoing(final.edges, nodeId).length === 0) continue;
    issues.push({
      code: "unexpected-source-only-node",
      nodeId,
      ...(finalNode.label ? { previousLabel: finalNode.label } : {}),
      message: `Node ${nodeId} lost every incoming relationship from the foundation but still feeds the final causal chain`,
    });
  }
  for (const [nodeId, node] of final.nodes) {
    const branches = outgoing(final.edges, nodeId);
    if (branches.length < 2 || !/(?:attempt|choice|decision|condition|whether|check)/i.test(node.label ?? "")) continue;
    if (branches.filter((edge) => !edge.label).length < 2) continue;
    issues.push({
      code: "ambiguous-conditional-branch",
      nodeId,
      ...(node.label ? { previousLabel: node.label } : {}),
      message: `Conditional-looking node ${nodeId} has multiple unlabeled outcomes`,
    });
  }
  return { category: issues.length ? "structurally-suspicious" : "valid", issues };
}

export async function assertMermaidIntegrity(source: string, expectedType: DiagramType, baseSource?: string): Promise<void> {
  const report = await inspectMermaidIntegrity(source, expectedType, baseSource);
  if (report.issues.length) throw new Error(`Mermaid source is structurally suspicious: ${report.issues.map((issue) => issue.nodeId).join(", ")}`);
}

type MermaidParser = {
  parser?: { yy?: unknown };
  yy?: unknown;
  parse(source: string): unknown | Promise<unknown>;
};

type MermaidDiagramModule = { diagram: { parser: MermaidParser; db?: unknown } };

const noop = (): void => undefined;
const enumValues = new Proxy<Record<string, string>>({}, { get: (_target, property) => String(property) });

function grammarContext(type: DiagramType, flowCollector?: FlowStructureCollector): Record<string, unknown> | undefined {
  if (type === "flowchart") {
    let firstGraph = true;
    return {
      addClass: noop,
      addLink: (starts: unknown, ends: unknown, linkData: unknown) => {
        if (flowCollector) recordFlowLinks(flowCollector, starts, ends, linkData);
        return 0;
      },
      addSubGraph: noop,
      addVertex: (id: unknown, text: unknown, vertexType: unknown) => {
        if (flowCollector) recordFlowNode(flowCollector, id, text, vertexType);
      },
      destructLink: () => ({ type: "arrow_point", stroke: "normal", length: 1 }),
      lex: { firstGraph: () => firstGraph ? ((firstGraph = false), true) : false },
      setAccDescription: noop,
      setAccTitle: noop,
      setClass: noop,
      setClickEvent: noop,
      setDirection: noop,
      setLink: noop,
      setTooltip: noop,
      updateLink: noop,
      updateLinkInterpolate: noop,
    };
  }
  if (type === "mindmap") {
    return {
      getLogger: () => ({ trace: noop, info: noop }),
      addNode: noop,
      decorateNode: noop,
      getType: () => 0,
      nodeType: { DEFAULT: 0 },
    };
  }
  if (type === "sequence") {
    return {
      apply: noop,
      parseBoxData: () => ({}),
      parseMessage: (value: string) => value,
      setAccDescription: noop,
      setAccTitle: noop,
      setDiagramTitle: noop,
      LINETYPE: enumValues,
      PLACEMENT: enumValues,
    };
  }
  if (type === "state") {
    return {
      getDividerId: () => "divider",
      setAccDescription: noop,
      setAccTitle: noop,
      setDirection: noop,
      setRootDoc: noop,
      trimColon: (value: string) => value.replace(/^\s*:\s*/, "").trim(),
    };
  }
  if (type === "class") {
    const passthrough = (value: string) => value;
    return {
      addAnnotation: noop,
      addClass: noop,
      addClassesToNamespace: noop,
      addMember: noop,
      addMembers: noop,
      addNamespace: passthrough,
      addNote: () => ({}),
      addRelation: noop,
      cleanupLabel: passthrough,
      defineClass: noop,
      lineType: enumValues,
      popNamespace: noop,
      relationType: enumValues,
      setAccDescription: noop,
      setAccTitle: noop,
      setClassLabel: noop,
      setClickEvent: noop,
      setCssClass: noop,
      setCssStyle: noop,
      setDirection: noop,
      setLink: noop,
      setTooltip: noop,
    };
  }
  if (type === "timeline") {
    return {
      addEvent: noop,
      addSection: noop,
      addTask: noop,
      getCommonDb: () => ({ setDiagramTitle: noop, setAccTitle: noop, setAccDescription: noop }),
      setDirection: noop,
    };
  }
  return undefined;
}

async function loadDiagram(type: DiagramType): Promise<MermaidDiagramModule> {
  // Mermaid does not expose its legacy grammars as a public parser-only API. These
  // version-pinned chunks let the Worker execute the same grammars as the browser
  // renderer without requiring a DOM. Keep Mermaid pinned when changing these paths.
  switch (type) {
    case "flowchart": return import("mermaid/dist/chunks/mermaid.core/flowDiagram-IUGLLWBU.mjs");
    case "mindmap": return import("mermaid/dist/chunks/mermaid.core/mindmap-definition-6OY57UG3.mjs");
    case "sequence": return import("mermaid/dist/chunks/mermaid.core/sequenceDiagram-BLEHNHQU.mjs");
    case "state": return import("mermaid/dist/chunks/mermaid.core/stateDiagram-v2-A6IESKWZ.mjs");
    case "class": return import("mermaid/dist/chunks/mermaid.core/classDiagram-v2-4MYWR3UA.mjs");
    case "timeline": return import("mermaid/dist/chunks/mermaid.core/timeline-definition-JXYKGLGB.mjs");
  }
}

async function parseWithMermaidGrammar(source: string, type: DiagramType, flowCollector?: FlowStructureCollector): Promise<void> {
  const { diagram } = await loadDiagram(type);
  const parser = diagram.parser;
  const context = grammarContext(type, flowCollector) ?? diagram.db;
  if (!context) throw new Error(`No parser context is available for ${type}`);
  if (parser.parser) parser.parser.yy = context;
  parser.yy = context;
  await parser.parse(source);
}

